import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Receipt } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

const CHANNEL_LABEL = { edisplay: "eDisplay", infinity: "Infinity Touch", funeral: "Funeral Store" };
const STATUS_STYLE = {
  available: "border-green-200 bg-green-50 text-green-700",
  pending: "border-amber-200 bg-amber-50 text-amber-700",
  paid_out: "border-slate-200 bg-slate-50 text-slate-600",
};

export default function CommissionLedger() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/reseller/commission-ledger`);
        setEntries(res.data || []);
      } catch (e) {
        toast.error("Failed to load commission ledger");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const total = entries.reduce((s, e) => s + Number(e.amount || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="commission-ledger">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Receipt className="w-6 h-6" /> Commission Ledger
          </h2>
          <p className="text-slate-500">Your commission earned per order across channels.</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-500">Total commission</p>
          <p className="text-2xl font-bold text-slate-900">{money(total)}</p>
        </div>
      </div>

      <div className="card-elevated overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold">Order #</TableHead>
              <TableHead className="font-semibold">Channel</TableHead>
              <TableHead className="font-semibold">Order Total</TableHead>
              <TableHead className="font-semibold">Commission</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold">Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">Loading…</TableCell></TableRow>
            ) : entries.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">No commission entries yet.</TableCell></TableRow>
            ) : entries.map((e) => (
              <TableRow key={e.transaction_id} className="table-row">
                <TableCell className="font-medium">{e.order_number || e.order_id}</TableCell>
                <TableCell>{CHANNEL_LABEL[e.channel] || e.channel || "—"}</TableCell>
                <TableCell>{money(e.order_total)}</TableCell>
                <TableCell className="font-semibold text-green-700">{money(e.amount)}</TableCell>
                <TableCell><Badge variant="outline" className={STATUS_STYLE[e.status] || ""}>{e.status}</Badge></TableCell>
                <TableCell>{fmtDate(e.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
